import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, XCircle, ExternalLink, FileText, RefreshCw, AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, API } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import AnalyticsPanel from "@/components/AnalyticsPanel";
import { DeletionRequests } from "@/components/DeletionRequests";
import { DuplicateAccounts } from "@/components/DuplicateAccounts";
import { SplitSignupRepair } from "@/components/SplitSignupRepair";
import { WhatsAppFailures, failureText } from "@/components/WhatsAppFailures";
import { AccountHistory } from "@/components/AccountHistory";
import { usePagedList, ListToolbar, Pager } from "@/components/PagedList";

const STATUS_COLOR = {
  pending: "bg-amber-100 text-amber-800",
  verified: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800",
};

export default function AdminPage() {
  const pros = usePagedList("/admin/professionals");
  const shifts = usePagedList("/admin/shifts");
  const [openRequests, setOpenRequests] = useState(0);
  const [historyKey, setHistoryKey] = useState(0);

  const loadRequests = async () => {
    try {
      const { data } = await api.get("/admin/deletion-requests");
      setOpenRequests(data.filter(r => r.status === "open").length);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load");
    }
  };
  useEffect(() => { loadRequests(); }, []);
  const load = () => { pros.reload(); shifts.reload(); loadRequests(); setHistoryKey(k => k + 1); };

  const setStatus = async (userId, status, reason, rejectedKind) => {
    try {
      await api.post(`/admin/professionals/${userId}/verify`, { status, rejection_reason: reason, rejected_kind: rejectedKind || undefined });
      toast.success(`Marked ${status}`);
      await pros.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <AppHeader subtitle="Admin console" />
      <div className="max-w-6xl mx-auto px-5 py-8 md:py-10 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Admin</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Verification queue</h1>
          </div>
          <Button variant="outline" size="sm" onClick={load} className="rounded-full" data-testid="admin-refresh"><RefreshCw className="w-4 h-4 mr-1.5" /> Refresh</Button>
        </div>

        <Tabs defaultValue="analytics">
          <TabsList className="rounded-full h-auto min-h-11 p-1 bg-slate-100 flex-wrap">
            <TabsTrigger value="analytics" data-testid="tab-analytics" className="rounded-full">Analytics</TabsTrigger>
            <TabsTrigger value="professionals" data-testid="tab-professionals" className="rounded-full">Professionals ({pros.total}{pros.q || pros.status !== "all" ? " matching" : ""})</TabsTrigger>
            <TabsTrigger value="shifts" data-testid="tab-shifts" className="rounded-full">Shifts ({shifts.total}{shifts.q || shifts.status !== "all" ? " matching" : ""})</TabsTrigger>
            <TabsTrigger value="whatsapp" data-testid="tab-whatsapp" className="rounded-full">WhatsApp</TabsTrigger>
            <TabsTrigger value="data-requests" data-testid="tab-data-requests" className="rounded-full">Data requests{openRequests ? ` (${openRequests})` : ""}</TabsTrigger>
            <TabsTrigger value="duplicates" data-testid="tab-duplicates" className="rounded-full">Duplicates</TabsTrigger>
          </TabsList>

          <TabsContent value="duplicates" className="mt-5 space-y-8">
            <SplitSignupRepair onDone={load} />
            <DuplicateAccounts onMerged={load} />
            <AccountHistory refreshKey={historyKey} />
          </TabsContent>

          <TabsContent value="whatsapp" className="mt-5">
            <h3 className="font-display text-lg font-semibold mb-3">Failed WhatsApp sends</h3>
            <WhatsAppFailures refreshKey={historyKey} />
          </TabsContent>

          <TabsContent value="data-requests" className="mt-5">
            <DeletionRequests onCount={setOpenRequests} />
          </TabsContent>

          <TabsContent value="analytics" className="mt-5">
            <AnalyticsPanel />
          </TabsContent>

          <TabsContent value="professionals" className="mt-5 space-y-4">
            <ListToolbar list={pros} statuses={["pending", "verified", "rejected"]} placeholder="Search name, email, phone, specialty, city" testid="pros" />
            {pros.loading && <div className="text-sm text-slate-500">Loading…</div>}
            {!pros.loading && pros.items.length === 0 && <EmptyState msg={pros.q || pros.status !== "all" ? "No professionals match." : "No professionals yet."} />}
            {pros.items.map(p => <ProRow key={p.user_id} p={p} onVerify={setStatus} />)}
            <Pager list={pros} testid="pros" />
          </TabsContent>

          <TabsContent value="shifts" className="mt-5 space-y-4">
            <ListToolbar list={shifts} statuses={["open", "confirmed", "completed", "cancelled"]} placeholder="Search hospital, specialty, city, date" testid="shifts" />
            {!shifts.loading && shifts.items.length === 0 ? <EmptyState msg={shifts.q || shifts.status !== "all" ? "No shifts match." : "No shifts posted yet."} /> : (
              <div className="grid md:grid-cols-2 gap-4">
                {shifts.items.map(s => <ShiftCard key={s.shift_id} s={s} />)}
              </div>
            )}
            <Pager list={shifts} testid="shifts" />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function ShiftCard({ s }) {
  return (
    <Card className="p-5 rounded-2xl border-slate-200 bg-white" data-testid={`admin-shift-${s.shift_id}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-800">{s.specialty}</div>
        <Badge variant="outline">{s.status}</Badge>
      </div>
      <div className="font-display font-semibold mt-1">{s.facility_name}</div>
      <div className="text-sm text-slate-600 mt-1">{s.date} · {s.start_time}–{s.end_time} · {s.city}</div>
      <div className="text-sm text-slate-600">{s.location}</div>
      <div className="mt-2 font-display font-bold">₹{s.pay_inr.toLocaleString("en-IN")}</div>
    </Card>
  );
}

function EmptyState({ msg }) {
  return <Card className="p-8 text-center text-slate-500 border-dashed border-slate-300 rounded-2xl bg-white">{msg}</Card>;
}

function ProRow({ p, onVerify }) {
  const [reason, setReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [failedKind, setFailedKind] = useState("");
  const docLink = (doc) => `${API}/credentials/${doc.doc_id}/file`;
  return (
    <Card className="p-5 rounded-2xl border-slate-200 bg-white" data-testid={`pro-row-${p.user_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-display text-lg font-semibold truncate">{p.name}</span>
            <Badge className={STATUS_COLOR[p.credential_status] + " hover:" + STATUS_COLOR[p.credential_status]}>{p.credential_status}</Badge>
            {p.possible_duplicate_of?.length > 0 && (
              <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100" title={`Matches ${p.possible_duplicate_of.join(", ")}. See the Duplicates tab (admin accounts can't be merged)`} data-testid={`duplicate-badge-${p.user_id}`}>
                Possible duplicate
              </Badge>
            )}
          </div>
          <div className="text-sm text-slate-600 mt-1">{p.email}</div>
          <div className="text-xs text-slate-500 mt-1">
            {p.profile?.professional_type} · {p.profile?.specialty}{p.profile?.degree ? ` · ${p.profile.degree}` : ""} · {p.profile?.years_experience || 0} yrs · ₹{p.profile?.rate_inr_per_hour || 0}/hr · {p.profile?.phone}
          </div>
          <div className="text-xs text-slate-500 font-mono mt-1">Reg: {p.council_registration_number || "—"}</div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-full border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => setShowReject(v => !v)} data-testid={`reject-btn-${p.user_id}`}>
            <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
          </Button>
          <Button size="sm" className="rounded-full bg-emerald-700 hover:bg-emerald-800" onClick={() => onVerify(p.user_id, "verified")} data-testid={`verify-btn-${p.user_id}`}>
            <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Verify
          </Button>
        </div>
      </div>

      {p.whatsapp_failure && (
        <div className="text-xs text-rose-700 mt-3 flex items-start gap-1 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2" data-testid={`pro-wa-failure-${p.user_id}`}>
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span className="break-words min-w-0">Last WhatsApp message failed ({new Date(p.whatsapp_failure.at).toLocaleString("en-IN")}): {failureText(p.whatsapp_failure)}</span>
        </div>
      )}

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        {["council_registration", "degree", "government_id"].map(kind => {
          const doc = p.documents?.find(d => d.kind === kind);
          return (
            <div key={kind} className="flex items-center justify-between border border-slate-200 rounded-xl px-3 py-2.5 text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-slate-500 shrink-0" />
                <span className="text-slate-700 shrink-0">{kind === "degree" ? "Degree" : kind === "council_registration" ? "Council Reg" : "Govt ID"}:</span>
                <span className="truncate text-slate-500">{doc?.file_name || "not uploaded"}</span>
              </div>
              {doc && (
                <a href={docLink(doc)} target="_blank" rel="noreferrer" className="text-emerald-800 hover:underline text-xs inline-flex items-center gap-1" data-testid={`view-doc-${p.user_id}-${kind}`}>
                  Open <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          );
        })}
      </div>

      {showReject && (
        <div className="mt-4 space-y-2">
          <Select value={failedKind} onValueChange={setFailedKind}>
            <SelectTrigger className="rounded-xl" data-testid={`reject-kind-${p.user_id}`}>
              <SelectValue placeholder="Which document failed?" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="council_registration" data-testid={`reject-kind-option-council-${p.user_id}`}>Council registration</SelectItem>
              <SelectItem value="degree" data-testid={`reject-kind-option-degree-${p.user_id}`}>Degree certificate</SelectItem>
              <SelectItem value="government_id" data-testid={`reject-kind-option-govid-${p.user_id}`}>Government ID</SelectItem>
            </SelectContent>
          </Select>
          <Textarea placeholder="Reason for rejection (shown to the professional)" value={reason} onChange={e => setReason(e.target.value)} rows={2} className="rounded-xl" data-testid={`reject-reason-${p.user_id}`} />
          <Button size="sm" variant="destructive" className="rounded-full" onClick={() => onVerify(p.user_id, "rejected", reason, failedKind)} data-testid={`confirm-reject-${p.user_id}`}>
            Confirm rejection
          </Button>
        </div>
      )}
    </Card>
  );
}
